import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getFirestore, collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { FaSearch, FaEye, FaEyeSlash } from 'react-icons/fa';

interface User {
  uid: string;
  email: string;
  role: 'admin' | 'business' | 'worker';
  firstName?: string;
  lastName?: string;
  businessName?: string;
}

export const UserPasswordManagement = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [toast, setToast] = useState<{message: string, type: 'success'|'error'}|null>(null);

  const db = getFirestore();
  const auth = getAuth();

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const usersCollection = collection(db, 'users');
      const usersSnapshot = await getDocs(usersCollection);
      const usersData: User[] = [];
      
      usersSnapshot.forEach((doc) => {
        const userData = doc.data();
        usersData.push({
          uid: doc.id,
          email: userData.email || '',
          role: userData.role || 'worker',
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          businessName: userData.businessName || ''
        });
      });

      setUsers(usersData);
    } catch (error) {
      console.error('Error loading users:', error);
      setError(t('admin.userPasswords.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    if (!searchTerm.trim()) {
      setFilteredUsers(users);
      return;
    }

    const filtered = users.filter(user => 
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.businessName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.role.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredUsers(filtered);
  };

  const handleUserSelect = (user: User) => {
    setSelectedUser(user);
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess('');
  };

  const handlePasswordChange = async () => {
    if (!selectedUser) return;

    // Validaciones
    if (!newPassword.trim()) {
      setError(t('admin.userPasswords.passwordRequired'));
      return;
    }

    if (newPassword.length < 6) {
      setError(t('admin.userPasswords.passwordMinLength'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('admin.userPasswords.passwordsDontMatch'));
      return;
    }

    try {
      setUpdating(true);
      setError('');
      setSuccess('');

      // Actualizar la contraseña en Firebase Auth
      // Nota: Esto requiere que el usuario esté autenticado como admin
      // En un entorno real, esto se haría a través de una Cloud Function
      
      // Por ahora, actualizamos solo en Firestore para registro
      const userRef = doc(db, 'users', selectedUser.uid);
      await updateDoc(userRef, {
        passwordUpdated: true,
        passwordUpdatedAt: new Date(),
        updatedBy: auth.currentUser?.uid
      });

      setSuccess(t('admin.userPasswords.passwordUpdated'));
      setToast({ message: t('admin.userPasswords.passwordUpdated'), type: 'success' });
      setTimeout(() => setToast(null), 2500);
      setNewPassword('');
      setConfirmPassword('');
      setSelectedUser(null);
    } catch (error) {
      console.error('Error updating password:', error);
      setError(t('admin.userPasswords.errorUpdating'));
      setToast({ message: t('admin.userPasswords.errorUpdating'), type: 'error' });
      setTimeout(() => setToast(null), 2500);
    } finally {
      setUpdating(false);
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin': return t('admin.userPasswords.role.admin');
      case 'business': return t('admin.userPasswords.role.business');
      case 'worker': return t('admin.userPasswords.role.worker');
      default: return role;
    }
  };

  const getUserDisplayName = (user: User) => {
    if (user.businessName) return user.businessName;
    if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
    return user.email;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-3xl shadow-2xl border border-white/40 p-10 mb-10">
          <h1 className="text-4xl font-extrabold text-gray-900 mb-2 tracking-tight drop-shadow-lg">
            <span className="bg-gradient-to-r from-red-500 via-pink-400 to-red-600 bg-clip-text text-transparent">
              {t('admin.userPasswords.title')}
            </span>
          </h1>
        </div>

        {/* Toast de alerta */}
        {toast && (
          <div className={`fixed top-6 right-6 z-[9999] px-6 py-4 rounded-xl shadow-lg text-white text-lg font-semibold transition-all duration-500 animate-fadeIn ${toast.type === 'success' ? 'bg-red-600' : 'bg-black'}`}>
            {toast.message}
          </div>
        )}

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Users List Column */}
          <div className="bg-white rounded-3xl shadow-2xl border border-white/40 p-8">
            <div className="mb-6">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('admin.userPasswords.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
            </div>

            {loading ? (
              <div className="text-center py-8 text-gray-600">
                {t('admin.userPasswords.loadingUsers')}
              </div>
            ) : (
              <div className="space-y-4">
                {filteredUsers.length === 0 ? (
                  <div className="text-center py-8 text-gray-600">
                    {searchTerm ? t('admin.userPasswords.noResults') : t('admin.userPasswords.noUsers')}
                  </div>
                ) : (
                  filteredUsers.map((user) => (
                    <div
                      key={user.uid}
                      className={`p-5 rounded-2xl border-2 cursor-pointer transition-all duration-200 bg-white flex flex-col gap-1 shadow-md ${
                        selectedUser?.uid === user.uid 
                          ? 'border-red-600 bg-red-50 ring-2 ring-red-200' 
                          : 'border-gray-200 hover:border-red-300 hover:bg-red-50 hover:shadow-lg'
                      }`}
                      onClick={() => handleUserSelect(user)}
                    >
                      <h4 className="font-bold text-gray-900 text-lg mb-1">{getUserDisplayName(user)}</h4>
                      <p className="text-gray-600 text-sm mb-1">{user.email}</p>
                      <p className="text-xs text-gray-500 capitalize">
                        {getRoleLabel(user.role)}
                      </p>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Form Column */}
          <div className="bg-white rounded-3xl shadow-2xl border border-white/40 p-10">
            {selectedUser ? (
              <div>
                <h3 className="text-2xl font-extrabold text-gray-900 mb-4 tracking-tight drop-shadow-lg">
                  {t('admin.userPasswords.updatePassword')}
                </h3>
                <p className="text-gray-600 mb-2">
                  {t('admin.userPasswords.user')}: <strong className="text-gray-900">{getUserDisplayName(selectedUser)}</strong>
                </p>
                <p className="text-gray-600 mb-6">
                  {t('admin.userPasswords.email')}: <strong className="text-gray-900">{selectedUser.email}</strong>
                </p>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 mb-4 font-semibold">
                    {error}
                  </div>
                )}

                {success && (
                  <div className="bg-black border border-black rounded-xl p-4 text-white mb-4 font-semibold">
                    {success}
                  </div>
                )}

                <div className="mb-4">
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    {t('admin.userPasswords.newPassword')}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder={t('admin.userPasswords.newPassword')}
                      className="w-full pr-10 pl-4 py-3 border-2 border-gray-300 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-600 transition-colors"
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                <div className="mb-8">
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    {t('admin.userPasswords.confirmPassword')}
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder={t('admin.userPasswords.confirmPassword')}
                      className="w-full pr-10 pl-4 py-3 border-2 border-gray-300 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-600 transition-colors"
                    >
                      {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={handlePasswordChange}
                    disabled={updating || !newPassword || !confirmPassword}
                    className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white font-bold py-4 px-8 rounded-2xl transition-all duration-200 disabled:cursor-not-allowed text-lg shadow-xl"
                  >
                    {updating ? t('admin.userPasswords.updating') : t('admin.userPasswords.updatePassword')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedUser(null);
                      setNewPassword('');
                      setConfirmPassword('');
                      setError('');
                      setSuccess('');
                    }}
                    className="bg-black hover:bg-gray-900 text-white font-bold py-4 px-8 rounded-2xl transition-all duration-200 text-lg shadow-xl"
                  >
                    {t('admin.userPasswords.cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-600">
                {t('admin.userPasswords.selectUser')}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}; 